import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { ICart } from "../../../utils/types";
import { calculateCheckoutTotals } from "../utils/summary-card-helper";

type Props = {
  cart: ICart;
  isFreeDelivery?: boolean;
};

const SummaryCard = ({ cart, isFreeDelivery = false }: Props) => {
  const { productSubtotal, deliveryFee, amountBeforeDiscount, discountUsed, totalToCollect } =
    calculateCheckoutTotals(cart, isFreeDelivery);

  const currency = cart?.products?.[0]?.currency ?? "";
  const totalItems = cart?.products?.reduce((sum, p) => sum + (p.productQty ?? 0), 0) ?? 0;

  return (
    <div className="bg-white font-nunito flex-1 p-2 my-2 rounded-lg">
      <div className="flex flex-col">
        <h4 className="mb-2 text-gray-700 font-medium text-base">Order Summary</h4>

        <div className="space-y-2 flex flex-col border-b-2 border-gray-300 pb-2">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 font-medium">Items</span>
            <span className="text-gray-800 font-medium">{totalItems}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-gray-600 font-medium">Product subtotal</span>
            <span className="text-gray-800 font-medium">
              {currency} {productSubtotal.toFixed(2)}
            </span>
          </div>

          {cart?.delivery && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-medium">Delivery fee</span>
              <div className="flex flex-col items-end">
                <span className={`text-gray-800 font-medium ${isFreeDelivery ? "line-through" : ""}`}>
                  {currency} {Number(cart.delivery.price ?? 0).toFixed(2)}
                </span>
                {isFreeDelivery && (
                  <span className="text-[10px] bg-green-200 px-1 rounded font-medium text-green-600">
                    Free Delivery
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <span className="text-gray-600 font-medium">Subtotal</span>
            <span className="text-gray-800 font-medium">{currency} {amountBeforeDiscount.toFixed(2)}</span>
          </div>

          {discountUsed > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-medium">Discount</span>
              <span className="text-green-600 font-medium">-{currency} {discountUsed.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-3">
          <h3 className="font-semibold text-gray-800">Total amount to collect</h3>
          <h3 className="font-semibold text-gray-800">{currency} {totalToCollect.toFixed(2)}</h3>
        </div>

        {Number(cart?.totalResellerProfit ?? 0) > 0 && (
          <div className="flex flex-row justify-start mt-3 space-x-2">
            <InformationCircleIcon className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
            <div className="flex flex-col text-blue-600 text-sm">
              <span className="font-bold">Profit on Sales</span>
              <span>
                You will make {currency} {Number(cart?.totalResellerProfit ?? 0).toFixed(2)} profit after the product has been delivered.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryCard;
